import { Injectable } from '@angular/core';
import { TimeBlockItemBuilderService } from './time-block-item-builder.service';
import { TimeBlockCrudService } from '../crud/time-block-crud.service';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { TimeCoordinateMappingService } from '../../../time-mapping/time-coordinate-mapping.service';
import { CalendarService } from '../../../services/calendar.service';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Subject, merge, of, timer } from 'rxjs';
import { TimeBlockContentType, TimeBlockType } from '../types/time-block-types';
import { CalendarGeometryService } from '../../../services/calendar-geometry.service';
import { CalendarMouseHandlerService } from '../../../mouse/calendar-mouse-handler.service';
import { DayOrWeekCalendarModel } from '../../../../../core/models/calendar/day-or-week-calendar.model';
import { TouchAndMouseHandlerService } from '../../../../../core/services/touch-and-mouse-handler.service';
import { CalendarViews } from '../../../calendar-types';
import { longClickDuration } from '../time-block.constants';

@Injectable()
export class NewTimeBlockGenerationService {
  public emptySpaceMouseDown$ = new Subject();
  public readyForGeneration = false;
  private readonly timeBlockTypeDetected$ = new Subject();
  private readonly stop$ = new Subject();
  private readonly tick = longClickDuration;

  constructor(
    private readonly calendarService: CalendarService,
    private readonly calendarMouseHandlerService: CalendarMouseHandlerService,
    private readonly calendarGeometryService: CalendarGeometryService,
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly timeCoordinateMappingService: TimeCoordinateMappingService,
    private readonly touchAndMouseHandlerService: TouchAndMouseHandlerService,
    private readonly timeBlockCrudService: TimeBlockCrudService,
  ) {
    this.initCalendarMouseInteractions();
  }

  /**
   * User clicks on an empty space in the calendar, so a non-existing time block is created.
   */
  public buildUserGeneratedTimeBlock(
    calendarView: CalendarViews,
    date: Date,
    containerId: number,
    isFixIntervalLane: boolean,
  ): void {
    if (calendarView === CalendarViews.DayGrid || calendarView === CalendarViews.WeekGrid) {
      if (isFixIntervalLane) {
        this.buildDayOrWeekViewFixIntervalTimeBlock(
          date,
          containerId,
          TimeBlockContentType.Project,
        );
      } else {
        this.buildDayOrWeekViewVariableIntervalTimeBlock(
          date,
          containerId,
          TimeBlockContentType.Project,
        );
      }
    } else {
      this.buildMonthViewDayOrWeekVariableIntervalTimeBlock(
        date,
        containerId,
        TimeBlockContentType.Project,
      );
    }
  }

  private buildDayOrWeekViewVariableIntervalTimeBlock(
    date: Date,
    laneIndex: number,
    timeBlockContentType: TimeBlockContentType,
  ): void {
    this.timeCoordinateMappingService.pixelsPerMinuteCalculated$
      .pipe(
        take(1),
        filter((ppm) => !!ppm),
        switchMap((ppm) => {
          const mouseCalendarPosition =
            this.calendarMouseHandlerService.MouseClickPositionWrapper.mouseCalendarPosition;

          const startTime = this.timeCoordinateMappingService.toDateTime(
            mouseCalendarPosition.y,
            ppm,
            date,
          );

          const timeBlockDraggingDuration = (this.calendarService.model as DayOrWeekCalendarModel)
            .calendarProperties.timeBlockDraggingDuration;
          // Calculate the difference to the previous threshold to snap the time block start to an appropriate threshold
          // (e.g. 10:05, 10:10, 10:15, etc. but not 10:02, 10:07 etc.).
          const startMinutes =
            startTime.getMinutes() - (startTime.getMinutes() % timeBlockDraggingDuration);

          const start = new Date(date);
          start.setHours(startTime.getHours());
          start.setMinutes(startMinutes);

          let end = new Date(start);
          end = DateTimeHelper.addMinutes(
            end,
            this.calendarService.model.calendarProperties.minimumTimeBlockDuration,
          );
          return of(
            this.timeBlockItemBuilderService.buildDefaultTimeBlock(
              start,
              end,
              laneIndex,
              true,
              timeBlockContentType,
            ),
          );
        }),
      )
      .subscribe((generatedTimeBlock) => {
        generatedTimeBlock.timeBlockModel.type = TimeBlockType.NonExistingBlock;
        const nonExistingGhost = this.timeBlockItemBuilderService.buildFromExisting(
          generatedTimeBlock.timeBlockModel,
          timeBlockContentType,
        );
        // Insert the non-existing ghost block
        this.timeBlockCrudService.insertTimeBlock(nonExistingGhost);
      });
  }

  private buildDayOrWeekViewFixIntervalTimeBlock(
    date: Date,
    laneIndex: number,
    timeBlockContentType: TimeBlockContentType,
  ): void {
    const start = DateTimeHelper.startOfDay(date);
    const end = DateTimeHelper.startOfDay(date);

    const nonExistingGhost = this.timeBlockItemBuilderService.buildDefaultTimeBlock(
      start,
      end,
      laneIndex,
      false,
      timeBlockContentType,
    );

    nonExistingGhost.timeBlockModel.type = TimeBlockType.NonExistingBlock;
    nonExistingGhost.timeBlockModel.timeBlockViewType.geometryData.width =
      this.calendarGeometryService.getDayOrWeekFixIntervalLaneWidth();
    this.timeBlockCrudService.insertTimeBlock(nonExistingGhost);
  }

  private buildMonthViewDayOrWeekVariableIntervalTimeBlock(
    date: Date,
    laneIndex: number,
    timeBlockContentType: TimeBlockContentType,
  ): void {
    this.timeBlockTypeDetected$.pipe(take(1)).subscribe((isFixInterval) => {
      const isVariableInterval = !isFixInterval;
      const start = DateTimeHelper.mergeDateAndTime(
        date,
        this.calendarService.model.calendarProperties.workTimeStart, // Todo: Set real work time start here
      );
      const end = DateTimeHelper.mergeDateAndTime(
        date,
        this.calendarService.model.calendarProperties.workTimeEnd, // Todo: Set real work time end here
      );

      const nonExistingGhost = this.timeBlockItemBuilderService.buildDefaultTimeBlock(
        start,
        end,
        laneIndex,
        isVariableInterval,
        timeBlockContentType,
      );

      nonExistingGhost.timeBlockModel.type = TimeBlockType.NonExistingBlock;
      this.timeBlockCrudService.insertTimeBlock(nonExistingGhost);
    });
  }

  private initCalendarMouseInteractions() {
    const stopCondition = merge(this.stop$);

    this.emptySpaceMouseDown$
      .pipe(
        filter((val) => val === true),
        switchMap(() => {
          return merge(
            timer(this.tick, this.tick)
              .pipe(takeUntil(stopCondition))
              .pipe(map(() => true)),
            this.touchAndMouseHandlerService.globalMouseReleased$.pipe(map(() => false)),
            this.touchAndMouseHandlerService.globalMouseMoved$.pipe(map(() => true)),
          ).pipe(take(1));
        }),
      )
      .subscribe((isFixInterval) => {
        if (this.calendarService.model.calendarViewMode === CalendarViews.MonthGrid) {
          this.timeBlockTypeDetected$.next(isFixInterval);
        }
        this.stop$.next(true);
      });
  }
}
